import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  Input,
  toast,
} from '@/components/ui'
import { cn } from '@/lib/utils'

import type { BillingOverview, BillingPlan } from '../billingApi'
import { useBillingErrorText } from '../billingErrors'
import { formatCount } from '../format'
import { useChangePlan, useSubscribe, useUpdateBillingProfile } from '../queries'

function PlanLimits({ plan }: { readonly plan: BillingPlan }) {
  const { t, i18n } = useTranslation()
  const e = plan.entitlements
  const fmt = (v: number) => formatCount(v, i18n.language)
  const lines: string[] = []
  if (e['ai.interactions.month'] !== undefined) {
    lines.push(t('billing.plans.limits.interactions', { value: fmt(e['ai.interactions.month']) }))
  }
  if (e['channels.max'] !== undefined) {
    lines.push(t('billing.plans.limits.channels', { value: fmt(e['channels.max']) }))
  }
  if (e['agents.max'] !== undefined) {
    lines.push(t('billing.plans.limits.agents', { value: fmt(e['agents.max']) }))
  }
  if (e['users.max'] !== undefined) {
    lines.push(t('billing.plans.limits.users', { value: fmt(e['users.max']) }))
  }
  if (e['knowledge.storage.mb'] !== undefined) {
    lines.push(t('billing.plans.limits.storage', { value: fmt(e['knowledge.storage.mb']) }))
  }
  if (e['triage.enabled'] !== undefined) {
    lines.push(
      e['triage.enabled'] > 0
        ? t('billing.plans.limits.triageOn')
        : t('billing.plans.limits.triageOff'),
    )
  }
  if (lines.length === 0) {
    lines.push(t('billing.plans.limits.custom'))
  }

  return (
    <ul className="flex flex-col gap-1 text-sm text-foreground-secondary">
      {lines.map((line) => (
        <li key={line}>• {line}</li>
      ))}
    </ul>
  )
}

export function PlansSection({
  tenantId,
  overview,
}: {
  readonly tenantId: string
  readonly overview: BillingOverview
}) {
  const { t, i18n } = useTranslation()
  const errorText = useBillingErrorText()
  const subscribe = useSubscribe(tenantId)
  const change = useChangePlan(tenantId)
  const [profileFor, setProfileFor] = useState<string | null>(null)
  const live = overview.subscription
  const names = t('billing.plans.names', { returnObjects: true }) as Record<string, string>

  const price = (plan: BillingPlan) =>
    plan.monthlyPrice === null
      ? null
      : new Intl.NumberFormat(i18n.language, {
          style: 'currency',
          currency: overview.currency,
        }).format(plan.monthlyPrice)

  function start(plan: BillingPlan) {
    if (overview.profile === null) {
      setProfileFor(plan.code)
      return
    }
    subscribe.mutate(plan.code, {
      onError: (error) => {
        toast.error(errorText(error))
      },
    })
  }

  return (
    <section className="flex flex-col gap-3" aria-labelledby="billing-plans-title">
      <div className="flex flex-col gap-1">
        <h2 id="billing-plans-title" className="text-base font-semibold text-foreground-primary">
          {t('billing.plans.title')}
        </h2>
        <p className="text-sm text-foreground-muted">{t('billing.plans.subtitle')}</p>
      </div>

      {!overview.canManage ? <Alert variant="info" title={t('billing.plans.onlyOwner')} /> : null}
      {overview.canManage && !overview.gatewayConfigured ? (
        <Alert variant="warning" title={t('billing.plans.gatewayUnavailable')} />
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        {overview.plans.map((plan) => {
          const isCurrent = live?.planCode === plan.code
          const amount = price(plan)
          return (
            <Card
              key={plan.code}
              data-testid={`plan-${plan.code}`}
              className={cn(isCurrent && 'border-action-primary')}
            >
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle>{names[plan.code] ?? plan.code}</CardTitle>
                  {isCurrent ? (
                    <Badge variant={live.status === 'PendingFirstPayment' ? 'warning' : 'success'}>
                      {live.status === 'PendingFirstPayment'
                        ? t('billing.plans.awaitingPayment')
                        : t('billing.plans.current')}
                    </Badge>
                  ) : live?.scheduledPlanCode === plan.code ? (
                    <Badge variant="info">{t('billing.plans.scheduled')}</Badge>
                  ) : null}
                </div>
                <CardDescription>
                  {amount === null ? (
                    <span title={t('billing.plans.priceUnsetHint')}>
                      {t('billing.plans.priceUnset')}
                    </span>
                  ) : (
                    <span className="text-xl font-semibold text-foreground-primary">
                      {amount}
                      <span className="text-sm font-normal text-foreground-muted">
                        {t('billing.plans.perMonth')}
                      </span>
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <PlanLimits plan={plan} />
                {!plan.selfServe ? (
                  <a
                    href={`mailto:${overview.salesEmail}`}
                    className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                  >
                    {t('billing.plans.contactSales')}
                  </a>
                ) : live?.status === 'Active' ? (
                  // Troca de plano (03 §3): upgrade gera fatura da diferença; downgrade vale na renovação.
                  !isCurrent && !live.cancelAtPeriodEnd && live.scheduledPlanCode !== plan.code ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={
                        !plan.subscribable ||
                        !overview.canManage ||
                        !overview.gatewayConfigured ||
                        change.isPending
                      }
                      onClick={() => {
                        change.mutate(plan.code, {
                          onSuccess: (result) => {
                            toast.success(
                              result.invoice !== null
                                ? t('billing.plans.upgradeCreated')
                                : t('billing.plans.downgradeScheduled'),
                            )
                          },
                          onError: (error) => {
                            toast.error(errorText(error))
                          },
                        })
                      }}
                    >
                      {t('billing.plans.changeTo')}
                    </Button>
                  ) : null
                ) : (
                  <Button
                    size="sm"
                    disabled={
                      !plan.subscribable ||
                      !overview.canManage ||
                      !overview.gatewayConfigured ||
                      live !== null ||
                      subscribe.isPending
                    }
                    title={!plan.subscribable ? t('billing.plans.priceUnsetHint') : undefined}
                    onClick={() => {
                      start(plan)
                    }}
                  >
                    {subscribe.isPending && subscribe.variables === plan.code
                      ? t('billing.plans.subscribing')
                      : t('billing.plans.subscribe')}
                  </Button>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      <ProfileDialog
        tenantId={tenantId}
        open={profileFor !== null}
        current={overview.profile}
        submitLabel={
          profileFor !== null ? t('billing.profile.saveAndSubscribe') : t('billing.profile.save')
        }
        onClose={() => {
          setProfileFor(null)
        }}
        onSaved={() => {
          const code = profileFor
          setProfileFor(null)
          if (code !== null) {
            subscribe.mutate(code, {
              onError: (error) => {
                toast.error(errorText(error))
              },
            })
          }
        }}
      />
    </section>
  )
}

export function ProfileDialog({
  tenantId,
  open,
  current,
  submitLabel,
  onClose,
  onSaved,
}: {
  readonly tenantId: string
  readonly open: boolean
  readonly current: BillingOverview['profile']
  readonly submitLabel: string
  readonly onClose: () => void
  readonly onSaved: () => void
}) {
  const { t } = useTranslation()
  const errorText = useBillingErrorText()
  const update = useUpdateBillingProfile(tenantId)
  const ids = useId()
  const [legalName, setLegalName] = useState(current?.legalName ?? '')
  const [taxId, setTaxId] = useState('')
  const [email, setEmail] = useState(current?.email ?? '')
  const [error, setError] = useState<string | null>(null)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('billing.profile.title')}
      description={t('billing.profile.description')}
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          update.mutate(
            { legalName, taxId, email },
            {
              onSuccess: () => {
                toast.success(t('billing.profile.saved'))
                onSaved()
              },
              onError: (e) => {
                setError(errorText(e))
              },
            },
          )
        }}
      >
        <Input
          id={`${ids}-name`}
          label={t('billing.profile.legalName')}
          value={legalName}
          required
          maxLength={200}
          onChange={(e) => {
            setLegalName(e.target.value)
          }}
        />
        <Input
          id={`${ids}-tax`}
          label={t('billing.profile.taxId')}
          value={taxId}
          required
          inputMode="text"
          autoComplete="off"
          placeholder={current?.taxIdMasked}
          onChange={(e) => {
            setTaxId(e.target.value)
          }}
        />
        <Input
          id={`${ids}-email`}
          type="email"
          label={t('billing.profile.email')}
          value={email}
          required
          maxLength={254}
          onChange={(e) => {
            setEmail(e.target.value)
          }}
        />
        {error !== null ? <Alert variant="danger" title={error} /> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
